import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Phone,
  Settings,
  Building2,
  Handshake,
  CreditCard,
  GitMerge,
  ClipboardList,
  Package,
  CheckSquare,
  Shield,
  UserCog,
  Globe,
  FileText,
  Clock,
  Target,
  Megaphone,
  Network,
  Award,
  Layers,
  ChevronDown,
  ChevronRight,
  LogOut,
  BarChart3,
  Calendar,
  PieChart,
  Activity,
  Zap,
  BookOpen,
  Filter,
  Key
} from 'lucide-react';

export default function Sidebar() {
  const [expandedCategories, setExpandedCategories] = useState([
    'TABLEAU DE BORD'
  ]);
  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [profile, setProfile] = useState(null);
  const location = useLocation();

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          console.log('Sidebar - Profil chargé:', profile.role);
          setProfile(profile);
          setUserRole(profile.role || 'user');
        }
      }
    } catch (error) {
      console.error('Erreur chargement user:', error);
    }
  };

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev =>
      prev.includes(categoryName)
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    window.location.href = '/login';
  };

  const canAccess = (item) => {
    const role = userRole?.toLowerCase() || 'user';
    
    if (role === 'super_admin' || role === 'admin' || role === 'superadmin') {
      return true;
    }
    
    if (role === 'manager') {
      if (item.name === 'Mes campagnes') return false;
      return true;
    }
    
    if (role === 'agent') {
      const agentItems = [
        'Dashboard',
        'Mes campagnes',
        'Leads',
        'Appels',
        'Calendrier',
        'Tâches'
      ];
      return agentItems.includes(item.name);
    }
    
    if (role === 'user') {
      return item.name === 'Dashboard';
    }
    
    return false;
  };

  const allCategories = [
    {
      name: 'TABLEAU DE BORD',
      icon: LayoutDashboard,
      items: [
        { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' }
      ]
    },
    {
      name: 'COMMERCIAL',
      icon: Target,
      items: [
        { name: 'Leads', icon: Users, path: '/leads', table: 'leads' },
        { name: 'Campagnes', icon: Megaphone, path: '/campaigns', table: 'campaigns' },
        { name: 'Mes campagnes', icon: Megaphone, path: '/agent-campaigns', table: 'campaigns' },
        { name: 'Appels', icon: Phone, path: '/calls', table: 'calls' }
      ]
    },
    {
      name: 'VENTES',
      icon: GitMerge,
      items: [
        { name: 'Deals', icon: GitMerge, path: '/deals', table: 'deals' },
        { name: 'Partenaires', icon: Handshake, path: '/partners', table: 'partners' },
        { name: 'Factures', icon: CreditCard, path: '/invoices', table: 'invoices' }
      ]
    },
    {
      name: 'OPÉRATIONS',
      icon: ClipboardList,
      items: [
        { name: 'Projets', icon: ClipboardList, path: '/projects', table: 'projects' },
        { name: 'Tâches', icon: CheckSquare, path: '/tasks', table: 'tasks' },
        { name: 'Stock', icon: Package, path: '/stock_items', table: 'stock_items' }
      ]
    },
    {
      name: 'ENTREPRISES',
      icon: Building2,
      items: [
        { name: 'Entreprises', icon: Building2, path: '/companies', table: 'companies' },
        { name: 'Partenariats', icon: Network, path: '/company_partnerships', table: 'company_partnerships' },
        { name: 'Profils', icon: UserCog, path: '/profiles', table: 'profiles' }
      ]
    },
    {
      name: 'ADMINISTRATION',
      icon: Shield,
      items: [
        { name: 'Utilisateurs', icon: Users, path: '/users', table: 'users' },
        { name: 'Rôles', icon: Shield, path: '/roles', table: 'company_roles' },
        { name: 'Permissions', icon: Key, path: '/permissions', table: 'permissions' },
        { name: 'Logs', icon: FileText, path: '/logs', table: 'audit_logs' }
      ]
    },
    {
      name: 'ANALYTIQUES',
      icon: BarChart3,
      items: [
        { name: 'Analytiques', icon: BarChart3, path: '/analytics' },
        { name: 'Rapports', icon: FileText, path: '/reports' },
        { name: 'Calendrier', icon: Calendar, path: '/calendar' },
        { name: 'Pipeline', icon: GitMerge, path: '/pipeline' },
        { name: 'Intégrations', icon: Globe, path: '/integrations' }
      ]
    },
    {
      name: 'CONFIGURATION',
      icon: Settings,
      items: [
        { name: 'Paramètres', icon: Settings, path: '/settings', table: 'settings' },
        { name: 'App Settings', icon: Globe, path: '/app-settings', table: 'app_settings' },
        { name: 'Global Settings', icon: Globe, path: '/global-settings', table: 'global_settings' }
      ]
    }
  ];

  const filteredCategories = allCategories
    .map(category => ({
      ...category,
      items: category.items.filter(item => canAccess(item))
    }))
    .filter(category => category.items.length > 0);

  if (!user) return null;

  return (
    <aside style={{
      width: '288px',
      height: '100vh',
      backgroundColor: '#111827',
      color: 'white',
      position: 'fixed',
      left: 0,
      top: 0,
      overflowY: 'auto',
      boxShadow: '2px 0 8px rgba(0,0,0,0.15)',
      zIndex: 50
    }}>
      {/* Logo */}
      <div style={{ padding: '24px', borderBottom: '1px solid #1f2937' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            background: 'linear-gradient(135deg, #06b6d4, #2563eb)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 'bold'
          }}>
            CRM
          </div>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', background: 'linear-gradient(135deg, #22d3ee, #60a5fa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Pro Center
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>Enterprise v3.0</div>
          </div>
        </div>
      </div>

      {/* Profil utilisateur */}
      <div style={{ padding: '16px', borderBottom: '1px solid #1f2937', backgroundColor: 'rgba(31,41,55,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ position: 'relative' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #06b6d4, #2563eb)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold'
            }}>
              {user.email?.[0]?.toUpperCase() || 'U'}
            </div>
            <div style={{
              position: 'absolute',
              bottom: '-4px',
              right: '-4px',
              width: '12px',
              height: '12px',
              backgroundColor: '#10b981',
              borderRadius: '50%',
              border: '2px solid #111827'
            }}></div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: '14px', fontWeight: '500', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.email}
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <p style={{ fontSize: '12px', color: '#22d3ee' }}>Connecté</p>
              <span style={{
                fontSize: '10px',
                padding: '2px 6px',
                backgroundColor: '#374151',
                borderRadius: '9999px',
                color: '#d1d5db',
                textTransform: 'capitalize'
              }}>
                {userRole}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Menu */}
      <nav style={{ padding: '16px 12px', overflowY: 'auto', height: 'calc(100vh - 200px)' }}>
        {filteredCategories.map((category) => {
          const isExpanded = expandedCategories.includes(category.name);
          const CategoryIcon = category.icon;
          
          return (
            <div key={category.name} style={{ marginBottom: '12px' }}>
              <button
                onClick={() => toggleCategory(category.name)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'transparent',
                  color: '#d1d5db',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <CategoryIcon size={18} color="#9ca3af" />
                  <span style={{ fontSize: '14px', fontWeight: '500' }}>{category.name}</span>
                </div>
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>

              {isExpanded && (
                <div style={{ marginLeft: '30px', marginTop: '4px', borderLeft: '1px solid #1f2937', paddingLeft: '12px' }}>
                  {category.items.map((item) => {
                    const isActive = location.pathname === item.path;
                    
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          color: isActive ? 'white' : '#9ca3af',
                          backgroundColor: isActive ? '#2563eb' : 'transparent',
                          fontSize: '14px',
                          marginBottom: '2px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = '#1f2937';
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <item.icon size={16} style={{ marginRight: '12px' }} />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Déconnexion */}
      <div style={{ padding: '16px', borderTop: '1px solid #1f2937', position: 'sticky', bottom: 0, backgroundColor: '#111827' }}>
        <button
          onClick={handleLogout}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px',
            borderRadius: '8px',
            border: 'none',
            background: 'transparent',
            color: '#9ca3af',
            fontSize: '14px',
            cursor: 'pointer',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#dc2626';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = '#9ca3af';
          }}
        >
          <LogOut size={18} />
          <span>Déconnexion</span>
        </button>
      </div>
    </aside>
  );
}
